import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { EnterpriseGate } from "@/components/enterprise-gate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, CheckSquare, ClipboardList, Check } from "lucide-react";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth";
import { readJson } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function api<T = unknown>(path: string, opts?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { credentials: "include", headers: { "Content-Type": "application/json" }, ...opts });
  return readJson<T>(response);
}

export default function Checklists() {
  return (
    <EnterpriseGate
      feature="Checklist Management"
      description="Create daily/weekly task checklists for your team, track completions, and ensure compliance. Available on the Enterprise plan."
    >
      <ChecklistsContent />
    </EnterpriseGate>
  );
}

function ChecklistsContent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const today = new Date().toISOString().split("T")[0];
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeChecklist, setActiveChecklist] = useState<any>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const { data: checklists = [], isLoading } = useQuery<any[]>({
    queryKey: ["checklists"],
    queryFn: async () => {
      const result = await api<unknown>("/api/checklists");
      if (!Array.isArray(result)) throw new Error("Unexpected checklists response");
      return result.map(cl => ({ ...cl, items: Array.isArray(cl.items) ? cl.items : [] }));
    },
  });

  const { data: todayLogs = [] } = useQuery<any[]>({
    queryKey: ["checklist-logs", activeChecklist?.id, today],
    queryFn: async () => {
      const result = await api<unknown>(`/api/checklists/${activeChecklist.id}/logs?date=${today}`);
      if (!Array.isArray(result)) throw new Error("Unexpected checklist logs response");
      return result;
    },
    enabled: !!activeChecklist,
  });

  const createChecklist = useMutation({
    mutationFn: (data: any) => api("/api/checklists", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["checklists"] }); setIsCreateOpen(false); },
    onError: () => toast({ title: "Failed to create checklist", variant: "destructive" }),
  });

  const deleteChecklist = useMutation({
    mutationFn: (id: number) => api(`/api/checklists/${id}`, { method: "DELETE" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["checklists"] }); setActiveChecklist(null); },
    onError: () => toast({ title: "Failed to delete checklist", variant: "destructive" }),
  });

  const submitLog = useMutation({
    mutationFn: ({ id, completedItems }: { id: number; completedItems: string[] }) =>
      api(`/api/checklists/${id}/logs`, {
        method: "POST",
        body: JSON.stringify({ date: today, completedItems, completedBy: user?.name }),
      }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["checklist-logs"] }); setChecked({}); },
    onError: () => toast({ title: "Failed to submit checklist", variant: "destructive" }),
  });

  const todayLog = (todayLogs as any[])[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Checklists</h1>
          <p className="text-muted-foreground mt-1">Daily and weekly task checklists for your team.</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" /> New Checklist
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Checklist list */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (checklists as any[]).length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-10 text-center">
                <ClipboardList className="w-10 h-10 text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">No checklists yet. Create your first one!</p>
              </CardContent>
            </Card>
          ) : (
            (checklists as any[]).map((cl: any) => (
              <Card
                key={cl.id}
                className={`cursor-pointer transition-all ${activeChecklist?.id === cl.id ? "ring-2 ring-primary" : "hover:shadow-sm"}`}
                onClick={() => { setActiveChecklist(cl); setChecked({}); }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">{cl.title}</CardTitle>
                    <Badge variant="secondary" className="text-xs">{cl.frequency}</Badge>
                  </div>
                  {cl.description && <CardDescription className="text-xs">{cl.description}</CardDescription>}
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground">{cl.items.length} items</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Active checklist */}
        <div className="md:col-span-2">
          {!activeChecklist ? (
            <Card className="h-full border-dashed flex flex-col items-center justify-center min-h-[300px]">
              <CheckSquare className="w-12 h-12 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Select a checklist to complete</p>
            </Card>
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{activeChecklist.title}</CardTitle>
                  <CardDescription>{format(new Date(), "EEEE, MMMM d")} — {activeChecklist.frequency}</CardDescription>
                </div>
                <div className="flex gap-2">
                  {todayLog && (
                    <Badge variant="default" className="gap-1">
                      <Check className="w-3 h-3" /> Submitted today
                    </Badge>
                  )}
                  <Button
                    variant="ghost" size="icon" className="text-destructive h-8 w-8"
                    onClick={() => deleteChecklist.mutate(activeChecklist.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(activeChecklist.items as string[]).map((item: string, i: number) => {
                  const id = `item-${i}`;
                  const doneItem = todayLog?.completedItems?.includes(item) || checked[id];
                  return (
                    <div key={id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50">
                      <Checkbox
                        id={id}
                        checked={!!doneItem}
                        disabled={!!todayLog}
                        onCheckedChange={v => setChecked(prev => ({ ...prev, [id]: !!v }))}
                      />
                      <label htmlFor={id} className={`flex-1 text-sm cursor-pointer ${doneItem ? "line-through text-muted-foreground" : ""}`}>
                        {item}
                      </label>
                    </div>
                  );
                })}
                {!todayLog && (
                  <Button
                    className="w-full mt-4"
                    onClick={() => {
                      const completed = (activeChecklist.items as string[]).filter((_: string, i: number) => checked[`item-${i}`]);
                      submitLog.mutate({ id: activeChecklist.id, completedItems: completed });
                    }}
                    disabled={submitLog.isPending}
                  >
                    {submitLog.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Submit Checklist ({Object.values(checked).filter(Boolean).length}/{activeChecklist.items.length} complete)
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Checklist</DialogTitle></DialogHeader>
          <CreateChecklistForm onSubmit={(data) => createChecklist.mutate(data)} loading={createChecklist.isPending} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateChecklistForm({ onSubmit, loading }: { onSubmit: (data: any) => void; loading: boolean }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [items, setItems] = useState<string[]>(["", ""]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ title, description, frequency, items: items.filter(i => i.trim()) });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium">Title</label>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Opening Checklist" required className="mt-1" />
      </div>
      <div>
        <label className="text-sm font-medium">Description (optional)</label>
        <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Tasks for opening the store" className="mt-1" />
      </div>
      <div>
        <label className="text-sm font-medium">Frequency</label>
        <Select value={frequency} onValueChange={setFrequency}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium">Items</label>
        <div className="space-y-2 mt-1">
          {items.map((item, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={item}
                onChange={e => setItems(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                placeholder={`Task ${i + 1}`}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setItems(prev => [...prev, ""])}>
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={loading || !title || items.filter(i => i.trim()).length === 0}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Create Checklist
      </Button>
    </form>
  );
}
